import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { poseSource } from "@/features/mascot/mascot.poses";

/**
 * Loads the hero mascot *after* the page has painted.
 *
 * The mascot pulls in GSAP and its rig chunk — enough to show up on the landing page's
 * critical path if it is imported statically (Vite adds a modulepreload for it, and the hero's
 * first paint is already JS-bound on mobile). The lightweight idle-pose poster is present in
 * the initial HTML-driven render; the animated rig is deferred until the browser is idle and
 * replaces that poster only after its first artwork has decoded.
 *
 * The wrapper keeps `.hero-greeter` in the first paint so the greeter's box — and therefore the
 * hero grid — is laid out at its final size before the mascot arrives, avoiding any shift.
 */
const loadHeroMascot = () => import("./HeroMascot");
const HeroMascot = lazy(loadHeroMascot);

export function BunnyGreeter() {
  const [ready, setReady] = useState(false);
  const [rigReady, setRigReady] = useState(false);
  const handleRigReady = useCallback(() => setRigReady(true), []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Start the download immediately, but mount only once the browser is idle. These are two
    // different costs: fetching competes for bandwidth, mounting competes for main-thread time.
    // Gating the fetch behind requestIdleCallback made the chunk the third leg of a serial
    // waterfall — main bundle, then idle callback, then chunk, then artwork — which is what
    // left the hero visibly empty for a second on a slow connection.
    const fetched = loadHeroMascot();

    const idle = window.requestIdleCallback;
    if (idle) {
      const handle = idle(() => setReady(true), { timeout: 2000 });
      return () => window.cancelIdleCallback?.(handle);
    }
    // No requestIdleCallback (Safari): mount as soon as the chunk is in, rather than guessing.
    let alive = true;
    void fetched.then(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="hero-greeter" aria-hidden="true" data-rig-ready={rigReady ? "true" : "false"}>
      <span className="hero-greeter-poster">
        <span className="hero-greeter-poster-shadow" />
        <img src={poseSource("idle")} width="400" height="400" alt="" decoding="async" />
      </span>
      {ready && (
        <Suspense fallback={null}>
          <HeroMascot onReady={handleRigReady} />
        </Suspense>
      )}
    </div>
  );
}
